import { InfoOutlined } from '@mui/icons-material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	Box,
	Divider,
	FormControl,
	Grid,
	IconButton,
	MenuItem,
	Select,
	SelectChangeEvent,
	Slider,
	Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import commonStyle from '../../common/Common.module.scss';

import RegulationChangeList from '../RegulationChangeList/RegulationChangeList';

import BarChart from '../../../common/components/Charts/BarChart/BarChart';
import DoughnutChart from '../../../common/components/Charts/DoughnutChart/DoughnutChart';
import AlertComponent from '../../../common/components/ToastMessage/ToastMessage';
import {
	APP_GENERAL_ERROR_MSG,
	RegulationChangeText,
} from '../../../common/constants/TextContent';
import {
	calculateDuration,
	scrollToTop,
	toSentenceCase,
} from '../../../common/utils/utilFunctions';
import PageTitle from '../../../components/PageTitle/PageTitle';

import { getRegulatoryActivityData, getRegulatoryRiskData } from './api';
import { menuItems, options, optionsBar } from './api/mock';
import { IRegulatoryActivityGraph, IRegulatoryRiskGraph } from './api/type-def';
import RecentlyViewed from './RecentlyViewed/RecentlyViewed';

import styles from './RegulationChange.module.scss';

const { dataLow, dataMedium, dataHigh } = styles;

// date formate function
const formatDate = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const RegulationChange = () => {
	const [selectedOption, setSelectedOption] = useState(
		'View by: Last 6 months',
	);
	const [regulatoryRiskData, setRegulatoryRiskData] =
		useState<IRegulatoryRiskGraph | null>(null);
	const [regulatoryActivityData, setRegulatoryActivityData] = useState<
		IRegulatoryActivityGraph[] | null
	>(null);
	const [dounutChartData, setDounutChartData] = useState<any>(null);
	const [dounutChartLableData, setDounutChartLableData] = useState<any>([]);

	const [regActivityChartData, setRegActivityChartData] = useState({
		labels: [''],
		datasets: [
			{
				label: 'Low',
				data: [0],
				borderColor: 'transparent',
				backgroundColor: dataLow,
			},
			{
				label: 'Medium',
				data: [0],
				borderColor: 'transparent',
				backgroundColor: dataMedium,
			},
			{
				label: 'High',
				data: [0],
				backgroundColor: dataHigh,
			},
		],
	});

	const [toastMessage, setToastMessageLocal] = useState('');
	const [, setLoading] = useState<boolean>(false);
	const [isToastOpen, setIsToastOpen] = useState(false);

	const handleOptionChange = (e: SelectChangeEvent<string>) => {
		setSelectedOption(e.target.value);
	};

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessageLocal('');
	}, []);

	// function to call getRegulatoryRiskData api
	const fetchRegulatoryRiskGraphData = useCallback(async () => {
		setLoading(true);
		const currentDate = new Date();
		const previousDate = new Date();
		calculateDuration(currentDate, previousDate, selectedOption);
		const params = {
			startDate: formatDate(currentDate),
			endDate: formatDate(previousDate),
		};
		const resp = await getRegulatoryRiskData(params);
		if (resp?.responseBody) {
			setRegulatoryRiskData(resp.responseBody);
			setLoading(false);
		} else {
			setIsToastOpen(true);
			setToastMessageLocal(APP_GENERAL_ERROR_MSG);
			setLoading(false);
		}
	}, [selectedOption]);

	// function to call getRegulatoryActivityData api
	const fetchRegulatoryActivityGraphData = useCallback(async () => {
		setLoading(true);
		const currentDate = new Date();
		const previousDate = new Date();
		calculateDuration(currentDate, previousDate, selectedOption);

		const params = {
			startDate: formatDate(currentDate),
			endDate: formatDate(previousDate),
		};

		const resp = await getRegulatoryActivityData(params);
		if (resp?.responseBody) {
			setRegulatoryActivityData(resp.responseBody);
			setLoading(false);
		} else {
			setIsToastOpen(true);
			setToastMessageLocal(APP_GENERAL_ERROR_MSG);
			setLoading(false);
		}
	}, [selectedOption]);

	useEffect(() => {
		fetchRegulatoryRiskGraphData();
		fetchRegulatoryActivityGraphData();
	}, [selectedOption]);

	useEffect(() => {
		if (regulatoryRiskData) {
			setDounutChartData({
				datasets: [
					{
						data: [
							regulatoryRiskData.highRiskCount,
							regulatoryRiskData.mediumRiskCount,
							regulatoryRiskData.lowRiskCount,
						],
						backgroundColor: [dataHigh, dataMedium, dataLow],
						borderColor: ['transparent', 'transparent', 'transparent'],
						borderWidth: 10,
						total: regulatoryRiskData.totalRiskCount,
					},
				],
			});

			setDounutChartLableData([
				{
					id: '1',
					color: 'data-high',
					text: 'High',
					digit: regulatoryRiskData.highRiskCount,
				},
				{
					id: '2',
					color: 'data-medium',
					text: 'Medium',
					digit: regulatoryRiskData.mediumRiskCount,
				},
				{
					id: '3',
					color: 'data-low',
					text: 'Low',
					digit: regulatoryRiskData.lowRiskCount,
				},
			]);
		}
	}, [regulatoryRiskData]);

	useEffect(() => {
		if (regulatoryActivityData) {
			setRegActivityChartData({
				labels: regulatoryActivityData.map((data) => data.agencyName),
				datasets: [
					{
						label: 'Low',
						data: regulatoryActivityData.map((data) => data.lowRiskCount),
						borderColor: 'transparent',
						backgroundColor: dataLow,
					},
					{
						label: 'Medium',
						data: regulatoryActivityData.map((data) => data.mediumRiskCount),
						borderColor: 'transparent',
						backgroundColor: dataMedium,
					},
					{
						label: 'High',
						data: regulatoryActivityData.map((data) => data.highRiskCount),
						backgroundColor: dataHigh,
					},
				],
			});
		}
	}, [regulatoryActivityData]);

	useEffect(() => {
		scrollToTop();
	}, []);

	const isRegulationChangeViewByDropDownEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REGULATION_CHANGE_VIEWBY_DROPDOWN_ENABLED,
	);

	return (
		<div className={commonStyle['page-background']}>
			<PageTitle text={RegulationChangeText} />
			<Grid container spacing={2}>
				<Grid
					item
					className={styles['regulation-change-div']}
					data-testid="Card"
				>
					<Grid container className={styles['regulation-change-text']}>
						<Grid item className={styles['regulation-tab-alignment']}>
							<Grid container>
								{isRegulationChangeViewByDropDownEnabled && (
									<Grid
										item
										xs={12}
										sm={12}
										md={12}
										lg={12}
										xl={12}
										className={styles['regulation-change-dropdown-box']}
									>
										<FormControl
											className={styles['regulation-change-dropdown']}
											size="small"
										>
											<Box>
												<Select
													className={
														styles['regulation-change-dropdown-select ']
													}
													onChange={handleOptionChange}
													defaultValue="View by: Last 6 months"
													data-testid="Combobox"
													disabled
													IconComponent={ExpandMoreRoundedIcon}
												>
													{menuItems.map((item) => (
														<MenuItem value={item.value} key={item.id}>
															<div data-testid="dropDown-selected">
																{item.label}
															</div>
														</MenuItem>
													))}
												</Select>
											</Box>
										</FormControl>
									</Grid>
								)}
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									lg={12}
									xl={12}
									className={styles['regulation-change-chart-container']}
								>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
											<Box
												className={`${styles['regulation-change-charts']} ${styles['regulation-change-barchart-box']}`}
											>
												<Typography
													className={styles['regulation-change-header']}
												>
													Overall compliance rating
													<IconButton aria-label="settings" disabled>
														<InfoOutlined />
													</IconButton>
												</Typography>
												<div
													className={styles['regulation-change-barchart-chart']}
												>
													<div
														className={
															styles['regulation-change-barchart-number']
														}
													>
														<div>
															<span
																className={
																	styles['regulation-change-barchart-numerator']
																}
															>
																78
															</span>
															<span
																className={
																	styles[
																		'regulation-change-barchart-denominator'
																	]
																}
															>
																/100
															</span>
														</div>
													</div>
													<div className={styles['regulation-change-slider']}>
														<Slider
															value={78}
															min={0}
															max={100}
															valueLabelDisplay="off"
															disableSwap
															disabled
															data-testid="Slider"
														/>
														<div
															className={
																styles['regulation-change-barchart-lables']
															}
														>
															<Typography
																className={
																	styles['regulation-change-barchart-low-text']
																}
															>
																Low
															</Typography>
															<Typography
																className={
																	styles['regulation-change-barchart-high-text']
																}
															>
																High
															</Typography>
														</div>
													</div>
												</div>

												<Typography
													className={styles['card-footer-box']}
													data-testid="range-selected"
												>
													{selectedOption.split(':')[1].trim()}
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
											<Box
												className={`${styles['regulation-change-charts']} ${styles['regulation-change-donutchart-box']}`}
												data-testid="Donutchart"
											>
												<Typography
													className={styles['regulation-change-header']}
												>
													Regulatory risk
													<IconButton aria-label="settings" disabled>
														<InfoOutlined />
													</IconButton>
												</Typography>
												{dounutChartData &&
													dounutChartLableData &&
													dounutChartLableData.length && (
														<div
															className={styles['regulation-change-donutchart']}
															data-testid="doughnutChart"
														>
															<DoughnutChart
																data={dounutChartData}
																options={options}
															/>
															<div
																className={
																	styles['regulation-change-donutchart-values']
																}
															>
																{dounutChartLableData.map((label: any) => (
																	<div
																		key={label.id}
																		className={
																			styles[
																				'regulation-change-donutchart-values-labels'
																			]
																		}
																	>
																		<div
																			className={
																				styles[
																					'regulation-change-donutchart-values-labels-columns'
																				]
																			}
																		>
																			<div
																				className={
																					styles[
																						`regulation-change-donutchart-values-${label.color}-label`
																					]
																				}
																			/>
																			<div
																				className={
																					styles[
																						'regulation-change-donutchart-label-text'
																					]
																				}
																			>
																				{label.text}
																			</div>
																			<div
																				className={
																					styles[
																						'regulation-change-donutchart-values-number'
																					]
																				}
																			>
																				{label.digit}
																			</div>
																		</div>
																	</div>
																))}
															</div>
														</div>
													)}

												<Typography className={styles['card-footer-box']}>
													{selectedOption.split(':')[1].trim()}
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
											<Box
												className={`${styles['regulation-change-charts']} ${styles['regulation-change-bar-box']}`}
												data-testid="Barchart"
											>
												<div
													className={styles['regulation-change-charts-header']}
												>
													<Typography
														className={styles['regulation-change-header']}
													>
														Regulatory activity
														<IconButton aria-label="settings" disabled>
															<InfoOutlined />
														</IconButton>
													</Typography>
													<div
														className={styles['regulation-change-charts-text']}
													>
														{toSentenceCase('By Risk Levels')}
														<div
															className={
																styles['regulation-change-charts-redcolorbox']
															}
														>
															<span
																className={
																	styles[
																		'regulation-change-charts-redcolorboxtext'
																	]
																}
															>
																High
															</span>
														</div>
														<div
															className={
																styles[
																	'regulation-change-charts-yellowcolorbox'
																]
															}
														>
															<span
																className={
																	styles[
																		'regulation-change-charts-yellowcolorboxtext'
																	]
																}
															>
																Medium
															</span>
														</div>
														<div
															className={
																styles['regulation-change-charts-greycolorbox']
															}
														>
															<span
																className={
																	styles[
																		'regulation-change-charts-greycolorboxtext'
																	]
																}
															>
																Low
															</span>
														</div>
													</div>
												</div>
												<div className={styles['regulation-change-barchart']}>
													{regulatoryActivityData &&
														regulatoryActivityData.length > 0 && (
															<div data-testid="barChartData">
																<BarChart
																	options={optionsBar}
																	data={regActivityChartData}
																/>
															</div>
														)}
												</div>
												<Typography className={styles['card-footer-box']}>
													{selectedOption.split(':')[1].trim()}
												</Typography>
											</Box>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<Divider className={styles.divider} />
				<Grid item className={styles['regulation-change-second-div']}>
					<RecentlyViewed />
				</Grid>
				<Divider className={styles.divider} />
				<Grid item className={styles['regulation-change-third-div']}>
					<RegulationChangeList viewDataForRange={selectedOption} />
				</Grid>
			</Grid>
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</div>
	);
};

export default RegulationChange;
